import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/contexts/auth'
import { supabase } from '@/lib/supabase'
import { getTranslatedGenreName } from '@/lib/utils'
import type { Genre } from '@/types'

const USER_PREFERRED_GENRES_QUERY_KEY = 'user-preferred-genres'
const GENRE_SUGGESTIONS_QUERY_KEY = 'genre-suggestions'

/**
 * Hook to get user's preferred genres
 */
export function useUserPreferredGenres() {
  const { user } = useAuth()
  const { i18n } = useTranslation()
  const currentLanguage = i18n.language

  return useQuery({
    queryKey: [USER_PREFERRED_GENRES_QUERY_KEY, user?.id, currentLanguage],
    queryFn: async (): Promise<Genre[]> => {
      if (!user?.id) throw new Error('User not authenticated')

      const { data, error } = await supabase.rpc('get_user_preferred_genres', {
        target_user_uuid: user.id,
      })

      if (error) throw error

      // Apply translations to the returned data
      return (data || []).map((genre: Record<string, unknown>) => ({
        ...genre,
        name:
          getTranslatedGenreName(genre as unknown as Genre, currentLanguage) ||
          (genre.name as string),
      }))
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  })
}

/**
 * Hook to get genre suggestions based on user's current library
 */
export function useGenreSuggestions(maxSuggestions: number = 10) {
  const { user } = useAuth()
  const { i18n } = useTranslation()
  const currentLanguage = i18n.language

  return useQuery({
    queryKey: [GENRE_SUGGESTIONS_QUERY_KEY, user?.id, currentLanguage, maxSuggestions],
    queryFn: async (): Promise<(Genre & { book_count: number })[]> => {
      if (!user?.id) throw new Error('User not authenticated')

      const { data, error } = await supabase.rpc('suggest_preferred_genres_for_user', {
        target_user_uuid: user.id,
        max_suggestions: maxSuggestions,
      })

      if (error) throw error

      // Apply translations and include book_count
      return (data || []).map((genre: Record<string, unknown> & { book_count: number }) => ({
        ...genre,
        name:
          getTranslatedGenreName(genre as unknown as Genre, currentLanguage) ||
          (genre.name as string),
      }))
    },
    enabled: !!user?.id,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  })
}

/**
 * Hook to update user's preferred genres
 */
export function useUpdateUserPreferredGenres() {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (genreIds: string[]) => {
      if (!user?.id) throw new Error('User not authenticated')

      const { error } = await supabase.rpc('set_user_preferred_genres', {
        genre_ids: genreIds,
        target_user_uuid: user.id,
      })

      if (error) throw error
    },
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({
        queryKey: [USER_PREFERRED_GENRES_QUERY_KEY, user?.id],
      })
      queryClient.invalidateQueries({
        queryKey: [GENRE_SUGGESTIONS_QUERY_KEY, user?.id],
      })
      // Also invalidate the main genres query to refresh any cached data
      queryClient.invalidateQueries({
        queryKey: ['genres'],
      })
    },
    onError: (error) => {
      console.error('Failed to update preferred genres:', error)
    },
  })
}

/**
 * Hook to check if user has configured preferred genres
 */
export function useHasPreferredGenres() {
  const { data: preferredGenres, isLoading } = useUserPreferredGenres()

  return {
    hasPreferredGenres: (preferredGenres?.length ?? 0) > 0,
    preferredGenresCount: preferredGenres?.length ?? 0,
    isLoading,
  }
}

/**
 * Combined hook for preferred genres management with smart defaults
 */
export function usePreferredGenresManager() {
  const preferredGenresQuery = useUserPreferredGenres()
  const suggestionsQuery = useGenreSuggestions(15)
  const updateMutation = useUpdateUserPreferredGenres()
  const { hasPreferredGenres } = useHasPreferredGenres()

  const isFirstTimeUser = !preferredGenresQuery.isLoading && !hasPreferredGenres
  const shouldShowSuggestions =
    isFirstTimeUser && !suggestionsQuery.isLoading && (suggestionsQuery.data?.length ?? 0) > 0

  return {
    // Queries
    preferredGenres: preferredGenresQuery.data ?? [],
    suggestions: suggestionsQuery.data ?? [],
    isLoadingPreferred: preferredGenresQuery.isLoading,
    isLoadingSuggestions: suggestionsQuery.isLoading,

    // State
    hasPreferredGenres,
    isFirstTimeUser,
    shouldShowSuggestions,

    // Actions
    updatePreferredGenres: updateMutation.mutate,
    isUpdating: updateMutation.isPending,
    updateError: updateMutation.error,

    // Combined loading state
    isLoading: preferredGenresQuery.isLoading || suggestionsQuery.isLoading,
  }
}
